/**
 * System Health API Client
 *
 * Wrapper functions for all health monitoring endpoints from Task 2505 series.
 * Uses the shared KodeonApiClient for authenticated HTTP requests.
 */

import { api } from '@/lib/api'
import type {
  TimeWindow,
  Granularity,
  SystemHealthResponse,
  HealthHistoryResponse,
  InfraVerifyResponse,
  DLQStatusResponse,
  EventVolumeResponse,
  WebhookMetricsResponse,
  GatewayMetricsResponse,
  QueueStatsResponse,
  ErrorLogsResponse,
} from './types'

// ── API Paths ───────────────────────────────────────────────────────────────

const PATHS = {
  systemHealth: '/v1/admin/system/health',
  healthHistory: '/v1/admin/system/health/history',
  infraVerify: '/v1/admin/infra/verify',
  dlqStatus: '/v1/admin/pubsub/dlq',
  eventVolume: '/v1/admin/events/volume',
  webhookMetrics: '/v1/admin/webhooks/metrics',
  gatewayMetrics: '/v1/admin/gateway/metrics',
  queueStats: '/v1/admin/operations/queue',
  errorLogs: '/v1/admin/logs/errors',
} as const

// ── API Functions ───────────────────────────────────────────────────────────

/**
 * Fetch aggregated per-service health status.
 */
export async function fetchSystemHealth(): Promise<SystemHealthResponse> {
  return api.get<SystemHealthResponse>(PATHS.systemHealth)
}

/**
 * Fetch historical health snapshots.
 */
export async function fetchHealthHistory(
  window: TimeWindow = '24h',
): Promise<HealthHistoryResponse> {
  return api.get<HealthHistoryResponse>(PATHS.healthHistory, { window })
}

/**
 * Verify GCP infrastructure readiness.
 */
export async function fetchInfraVerify(
  project?: string,
): Promise<InfraVerifyResponse> {
  const params: Record<string, string> = {}
  if (project) params.project = project
  return api.get<InfraVerifyResponse>(PATHS.infraVerify, params)
}

/**
 * Fetch Pub/Sub dead letter queue depths.
 */
export async function fetchDLQStatus(): Promise<DLQStatusResponse> {
  return api.get<DLQStatusResponse>(PATHS.dlqStatus)
}

/**
 * Fetch time-bucketed event volume.
 */
export async function fetchEventVolume(
  window: TimeWindow = '24h',
  granularity?: Granularity,
  groupBy?: 'type' | 'source',
): Promise<EventVolumeResponse> {
  const params: Record<string, string> = { window }
  if (granularity) params.granularity = granularity
  if (groupBy) params.group_by = groupBy
  return api.get<EventVolumeResponse>(PATHS.eventVolume, params)
}

/**
 * Fetch webhook receipt metrics.
 */
export async function fetchWebhookMetrics(
  window: TimeWindow = '24h',
): Promise<WebhookMetricsResponse> {
  return api.get<WebhookMetricsResponse>(PATHS.webhookMetrics, { window })
}

/**
 * Fetch gateway request metrics.
 */
export async function fetchGatewayMetrics(): Promise<GatewayMetricsResponse> {
  return api.get<GatewayMetricsResponse>(PATHS.gatewayMetrics)
}

/**
 * Fetch operations queue statistics.
 */
export async function fetchQueueStats(): Promise<QueueStatsResponse> {
  return api.get<QueueStatsResponse>(PATHS.queueStats)
}

/**
 * Fetch aggregated error logs.
 *
 * The system-health dashboard wants every error in the queried window for
 * triage (not a sample). The window mirrors the page-level time range so
 * scoping stays consistent across charts, but is clamped to a maximum of
 * 24h client-side: anything longer (7d, 30d) caps at 24h to avoid pulling
 * weeks of errors at once. The backend caps the response at maxEntries
 * (500); when exceeded, `capped` is true and the UI renders "500+".
 */
export async function fetchErrorLogs(
  window: '1h' | '24h' | '7d' = '1h',
): Promise<ErrorLogsResponse> {
  return api.get<ErrorLogsResponse>(PATHS.errorLogs, { window })
}

// ── Batch Fetch ─────────────────────────────────────────────────────────────

export interface HealthDataBatch {
  systemHealth: SystemHealthResponse | null
  healthHistory: HealthHistoryResponse | null
  infraVerify: InfraVerifyResponse | null
  dlqStatus: DLQStatusResponse | null
  eventVolume: EventVolumeResponse | null
  webhookMetrics: WebhookMetricsResponse | null
  gatewayMetrics: GatewayMetricsResponse | null
  queueStats: QueueStatsResponse | null
  errorLogs: ErrorLogsResponse | null
  errors: Record<string, string>
}

/**
 * Fetch all health endpoints in parallel.
 * Returns partial data on individual endpoint failures.
 */
export async function fetchAllHealthData(
  window: TimeWindow = '24h',
): Promise<HealthDataBatch> {
  const errors: Record<string, string> = {}

  const results = await Promise.allSettled([
    fetchSystemHealth(),
    fetchHealthHistory(window),
    fetchInfraVerify(),
    fetchDLQStatus(),
    fetchEventVolume(window),
    fetchWebhookMetrics(window),
    fetchGatewayMetrics(),
    fetchQueueStats(),
    fetchErrorLogs(window === '7d' ? '7d' : window === '24h' ? '24h' : '1h'),
  ])

  const getValue = <T>(
    result: PromiseSettledResult<T>,
    key: string,
  ): T | null => {
    if (result.status === 'fulfilled') {
      return result.value
    }
    errors[key] = result.reason instanceof Error
      ? result.reason.message
      : String(result.reason)
    return null
  }

  return {
    systemHealth: getValue(results[0], 'systemHealth'),
    healthHistory: getValue(results[1], 'healthHistory'),
    infraVerify: getValue(results[2], 'infraVerify'),
    dlqStatus: getValue(results[3], 'dlqStatus'),
    eventVolume: getValue(results[4], 'eventVolume'),
    webhookMetrics: getValue(results[5], 'webhookMetrics'),
    gatewayMetrics: getValue(results[6], 'gatewayMetrics'),
    queueStats: getValue(results[7], 'queueStats'),
    errorLogs: getValue(results[8], 'errorLogs'),
    errors,
  }
}

// ── Time Window Helpers ─────────────────────────────────────────────────────

/**
 * Convert TimeRange UI selection to API TimeWindow.
 */
export function timeRangeToWindow(range: string): TimeWindow {
  switch (range) {
    case '1h':
      return '1h'
    case '24h':
      return '24h'
    case '7d':
      return '7d'
    case '30d':
      return '30d'
    default:
      return '24h'
  }
}

/**
 * Get appropriate granularity for a time window.
 */
export function getGranularityForWindow(window: TimeWindow): Granularity {
  switch (window) {
    case '1h':
    case '6h':
      return 'minute'
    case '24h':
      return 'hour'
    case '7d':
      return 'hour'
    case '30d':
      return 'day'
    default:
      return 'hour'
  }
}
